import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsService } from '../services/projects.service';
import type { CreateProjectPayload } from '../types/api.types';
import toast from 'react-hot-toast';

const PROJECTS_QUERY_KEY = 'projects';

export const useProjectById = (projectId: number) => {
  return useQuery({
    queryKey: [PROJECTS_QUERY_KEY, projectId],
    queryFn: () => projectsService.getProjectById(projectId),
    enabled: !!projectId,
  });
};

export const useProjects = () => {
  const queryClient = useQueryClient();

  // Fetch all projects
  const {
    data: projects,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [PROJECTS_QUERY_KEY],
    queryFn: projectsService.getProjects,
  });

  // Create project mutation
  const createProjectMutation = useMutation({
    mutationFn: (payload: CreateProjectPayload) => projectsService.createProject(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
      toast.success('Project created successfully!');
    },
    onError: () => {
      toast.error('Failed to create project');
    },
  });

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: (projectId: number) => projectsService.deleteProject(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
      toast.success('Project deleted successfully!');
    },
    onError: () => {
      toast.error('Failed to delete project');
    },
  });

  return {
    projects: projects || [],
    isLoading,
    error,
    refetch,
    createProject: createProjectMutation.mutateAsync,
    deleteProject: deleteProjectMutation.mutateAsync,
    isCreating: createProjectMutation.isPending,
    isDeleting: deleteProjectMutation.isPending,
  };
};

// Hook for fetching single project
export const useProject = (projectId: number) => {
  const {
    data: project,
    isLoading,
    error,
  } = useQuery({
    queryKey: [PROJECTS_QUERY_KEY, projectId],
    queryFn: () => projectsService.getProject(projectId),
    enabled: !!projectId,
  });

  return {
    project,
    isLoading,
    error,
  };
};
